/**
 * Front-end JavaScript
 *
 * The JavaScript code you place here will be processed by esbuild. The output
 * file will be created at `../theme/js/script.min.js` and enqueued in
 * `../theme/functions.php`.
 *
 * For esbuild documentation, please see:
 * https://esbuild.github.io/
 */
import { createApp } from 'vue';
import { pinia } from './utils/pinia';

import 'primeicons/primeicons.css';
import './navigation';
import './svg-banner';
import './tg-news-link';
import Control from './components/navbar/Control.vue';
import Game from './pages/Game.vue';
import ToastService from 'primevue/toastservice';
import Toast from 'primevue/toast';
import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import Download from './components/banner/Download.vue';
import Mobile from './components/navbar/Mobile.vue';
import App from './pages/App.vue';
import IndexSubMenu from './components/navbar/IndexSubMenu.vue';
import { showVuePopup } from './utils/showVuePopup';
import Register from './popup/Register.vue';
import PromotionPopup from './popup/PromotionPopup.vue';
import { manager } from './apis/manager';
import Lotto from './pages/Lotto.vue';
import Sport from './pages/Sport.vue';
import Recommended from './pages/Recommended.vue';
import CarouselGame1 from './components/CarouselGame1.vue';
import CarouselGame2 from './components/CarouselGame2.vue';
import KeyFilter from 'primevue/keyfilter';
import PromotionGrid from './components/PromotionGrid.vue';

const isHomePage = (): boolean => {
	const path = window.location.pathname;
	return path === '/' || path.endsWith('/index.html');
};

document.addEventListener('DOMContentLoaded', async () => {
	try {
		// Deposit promo pop-up: เด้งครั้งเดียวต่อวัน (ต่อ browser+prefix) หลัง login.
		// Login ตั้ง `deposit_promo_pending` แล้ว full-page redirect ไปหน้า play
		// (path ต่างกันรายไซต์) บล็อกนี้จึงรันตอนโหลดหน้าถัดไปโดยไม่อิง path — ต้องอยู่
		// ก่อน homepage guard ด้านล่าง. Best-effort: ล้มเหลวก็ข้าม ไม่บล็อกหน้า
		if (sessionStorage.getItem('deposit_promo_pending') === '1') {
			sessionStorage.removeItem('deposit_promo_pending');
			const resp = await manager.config();
			if (resp?.code === '0') {
				const deposit0 = resp.result?.deposit?.[0];
				const prefix = resp.result?.theme?.prefix ?? '';
				const now = new Date();
				const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
				const seenKey = `deposit_promo_seen:${prefix}`;
				if (localStorage.getItem(seenKey) !== today && deposit0?.image) {
					localStorage.setItem(seenKey, today);
					await showVuePopup(
						PromotionPopup,
						{ image: deposit0.image, alt: deposit0.name ?? '' },
						{ customClass: { popup: 'bg-transparent shadow-none' } },
					);
				}
			}
		}

		if (!isHomePage()) return;

		const params = new URLSearchParams(window.location.search);

		const action = (params.get('action') ?? '').trim().toLowerCase();
		const refMk  = (params.get('marketingRef') ?? '').trim().toLowerCase();
		const refAf  = (params.get('ref') ?? '').trim().toLowerCase();
		const refRecommend = (params.get('recommend') ?? '').trim().toLowerCase()

		const hasAnyRef = refMk !== '' || refAf !== '' || refRecommend !== '';

		if (action === 'register' && hasAnyRef) {
			await showVuePopup(
				Register,
				{ options: { marketingRefId: refMk, affiliationRefId: refAf, recommendRefId: refRecommend } },
				{
					customClass: {
						popup: 'bg-[url(https://168snc.sgp1.cdn.digitaloceanspaces.com/theme/bg-register.webp)] bg-size-[100%_100%] bg-center bg-transparent'
					}
				}
			);
			return;
		}

		// Manager-driven promotion pop-up (once per browser session). Best-effort:
		// any failure just skips the pop-up and never blocks the page.
		if (sessionStorage.getItem('promo_popup_shown') !== '1') {
			const resp = await manager.config();
			const popup = resp?.code === '0' ? resp.result?.promotionPopup : undefined;
			if (popup?.show && popup.image) {
				sessionStorage.setItem('promo_popup_shown', '1');
				await showVuePopup(
					PromotionPopup,
					{ image: popup.image, href: popup.href, alt: popup.alt },
					{ customClass: { popup: 'bg-transparent shadow-none' } },
				);
			}
		}
	} catch (err) {
		console.error('open homepage popup failed:', err);
	}
});

/**
 * App
 */
const elApp = document.getElementById('app')
if (elApp) {
	const app = createApp(App, {});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.directive('keyfilter', KeyFilter);
	app.component('Toast', Toast);
	app.mount('#app');
}

/**
 * Promotion Grid (manager-driven homepage promotions)
 */
const elPromotion = document.getElementById('promotion')
if (elPromotion) {
	const app = createApp(PromotionGrid, {});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#promotion');
}

/**
 * IndexSubMenu
 */
const elIndexSubMenu = document.getElementById('index-sub-menu');
if (elIndexSubMenu) {
	const app = createApp(IndexSubMenu, {});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#index-sub-menu');
}

/**
 * Banner Download
 */
const elBanner = document.getElementById('banner-download')
if (elBanner) {
	const app = createApp(Download, {});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#banner-download');
}

/**
 * Carousel 1
 */
const elCarousel1 = document.getElementById('carousel-game-1')
if (elCarousel1) {
	const app = createApp(CarouselGame1, {});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#carousel-game-1');
}

/**
 * Carousel 2
 */
const elCarousel2 = document.getElementById('carousel-game-2')
if (elCarousel2) {
	const app = createApp(CarouselGame2, {});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#carousel-game-2');
}

/**
 * Control
 */
const elControl = document.getElementById('control')
if (elControl) {
	const app = createApp(Control, {});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#control');
}

/**
 * Mobile
 */
const elMobile = document.getElementById('mobile-navbar')
if (elMobile) {
	const app = createApp(Mobile, {});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#mobile-navbar');
}

/**
 * Game
 */
const elGame = document.getElementById('game')
if (elGame) {
	const gameType = elGame.getAttribute('game-type') || '';
	const app = createApp(Game, {gameType: gameType});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#game');
}

/**
 * Lotto
 */
const elLotto = document.getElementById('lotto')
if (elLotto) {
	const gameType = elLotto.getAttribute('game-type') || '';
	const app = createApp(Lotto, {gameType: gameType});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#lotto');
}

/**
 * Sport
 */
const elSport = document.getElementById('sport')
if (elSport) {
	const gameType = elSport.getAttribute('game-type') || '';
	const app = createApp(Sport, {gameType: gameType});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#sport');
}

/**
 * Recommended
 */
const elRecommended = document.getElementById('recommended')
if (elRecommended) {
	const gameType = elRecommended.getAttribute('game-type') || '';
	const app = createApp(Recommended, {gameType: gameType});
	app.use(pinia);
	app.use(PrimeVue, {
		theme: {
			preset: Aura,
			options: {
				darkModeSelector: false,
			},
		},
	})
	app.use(ToastService)
	app.component('Toast', Toast);
	app.mount('#recommended');
}
